import React, { useState, useEffect, useMemo } from "react";
import classes from "./reviews.module.scss";
import EmptyReviews from "../../assets/imgs/reviews/emptyPage.png";

import axios from "../../requests/axios";
import routes from "../../requests/routes";
import GlobeLoader from "../../layouts/globeLoader/GlobeLoader";

import { ReviewFilters } from "./Filters/Filters";
import ReviewCard from "./reviewCard/ReviewCard";

// Static catalog for mapping type id → label
const TRIP_TYPES = [
  { id: 0, name: "Volunteering" },
  { id: 1, name: "Adventurous & Road trips" },
  { id: 2, name: "Have a Dream Juniors" },
  { id: 3, name: "EU program" },
  { id: 4, name: "Special Requests" },
];

const UserReviews = ({ isHosting }) => {
  const [reviewsData, setReviewsData] = useState([]);
  const [loading, setLoading] = useState(true);

  // filters shape: { location: string[], type: string[] }
  const [reviewFilter, setReviewFilter] = useState({
    location: [],
    type: [],
  });

  // ────────────────────────────────────────────────────────────────
  // Load reviews
  // ────────────────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;

    const getReviews = async () => {
      try {
        setLoading(true);

        // Only include query when we actually want to filter by hosting.
        const params = {};
        if (isHosting === true) params.isHosting = true;

        const { data } = await axios.get(routes.getReviews, { params });
        if (!cancelled && data?.success) {
          setReviewsData(Array.isArray(data.reviews) ? data.reviews : []);
        }
      } catch {
        if (!cancelled) setReviewsData([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    window.scrollTo(0, 0);
    getReviews();

    return () => {
      cancelled = true;
    };
  }, [isHosting]);

  // ────────────────────────────────────────────────────────────────
  // Derived filter sources
  // ────────────────────────────────────────────────────────────────
  const allDestinations = useMemo(() => {
    const set = new Set(
      (reviewsData || [])
        .map((r) => r?.program?.location)
        .filter(Boolean)
        .map(String)
    );
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [reviewsData]);

  // Available trip types from data (normalized as strings)
  const tripTypeOptions = useMemo(() => {
    const present = new Set(
      (reviewsData || [])
        .map((r) => r?.program?.type)
        .filter((v) => v !== undefined && v !== null && String(v).trim() !== "")
        .map((v) => String(v))
    );

    // Keep order from TRIP_TYPES, but only include present ones
    return TRIP_TYPES.filter((t) => present.has(String(t.id))).map((t) => ({
      id: String(t.id),
      label: t.name,
    }));
  }, [reviewsData]);

  // ────────────────────────────────────────────────────────────────
  // Filtering
  // ────────────────────────────────────────────────────────────────
  const filteredReviews = useMemo(() => {
    const src = Array.isArray(reviewsData) ? reviewsData : [];

    const types = Array.isArray(reviewFilter.type)
      ? reviewFilter.type
      : reviewFilter.type
      ? [reviewFilter.type]
      : [];
    const locations = Array.isArray(reviewFilter.location)
      ? reviewFilter.location
      : reviewFilter.location
      ? [reviewFilter.location]
      : [];

    const hasType = types.length > 0;
    const hasLocation = locations.length > 0;

    if (!hasType && !hasLocation) return src;

    const typeSet = new Set(types.map((t) => String(t)));
    const locSet = new Set(locations.map((s) => String(s).toLowerCase()));

    return src.filter((review) => {
      const reviewType = String(review?.program?.type ?? "");
      const reviewLoc = String(review?.program?.location || "").toLowerCase();

      const typeOk = !hasType || typeSet.has(reviewType);
      const locOk = !hasLocation || locSet.has(reviewLoc);

      return typeOk && locOk;
    });
  }, [reviewsData, reviewFilter.type, reviewFilter.location]);

  // ────────────────────────────────────────────────────────────────
  // Render
  // ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className={classes.container}>
        <GlobeLoader />
      </div>
    );
  }

  return (
    <div className={classes.container}>
      {/* Banner (hide for hosting section) */}
      {!isHosting && (
        <section className={"banner"}>
          <div className={"banner__overlay"}>
            <div className={"banner__pattern"} aria-hidden="true" />
            <div className={"banner__body"}>
              <h2 className={"banner__body__title"}>
                What past travellers say about their <span>Adventures</span>
              </h2>
              <p className={"banner__body__text"}>
                Stories from travelers who turned trips into memories—and
                friends into family.
              </p>
            </div>
          </div>
        </section>
      )}

      <div className={classes.content}>
        <ReviewFilters
          allDestinations={allDestinations}
          reviews={filteredReviews}
          reviewFilter={reviewFilter}
          setReviewFilter={setReviewFilter}
          tripTypeOptions={tripTypeOptions}
        />

        <div className={classes.reviews}>
          {filteredReviews.length > 0 ? (
            filteredReviews.map((review, i) => (
              <ReviewCard key={review?._id || i} review={review} />
            ))
          ) : (
            <div className={classes.emptyPage}>
              <h2>No Reviews Yet</h2>
              <img src={EmptyReviews} alt="No reviews" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserReviews;
